import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import commonMessages from 'i18n/commonMessages'
import styled from 'styled-components'
import { rem } from 'polished'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import TextButton from 'LuxKit/Button/TextButton'
import useCopyWithFeedback from 'hooks/useCopyWithFeedback'
import Label from 'LuxKit/Label/Label'
import { MIN_LABEL_HEIGHT } from 'LuxKit/Label/constants'
import ModalContext from 'contexts/ModalContext'
import PaginationDots from 'LuxKit/PaginationDots/PaginationDots'
import { CTA_PROMOTION, FLIGHT_VOUCHER_PROMOTION, PROMO_CODE_PROMOTION, type LandingPagePromotion } from 'luxPlus/constants/promotions'
import LuxPlusPromoCodeModal from '../modals/LuxPlusPromoCodeModal'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LuxPlusFlightVoucherModal from '../modals/LuxPlusFlightVoucherModal'

const CardWrapper = styled.div`
  position: relative;
  min-height: 100%;
  padding-top: calc(${MIN_LABEL_HEIGHT} / 2);
`

const Card = styled(Pane)<{ $minHeight?: number, $horizontalMargin?: number }>`
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border-radius: ${props => props.theme.borderRadius.L};
  border: 2px solid ${props => props.theme.palette.highlight.primary.normalForeground};
  margin-inline-start: ${props => props.$horizontalMargin ? rem(props.$horizontalMargin) : '0'};
  margin-inline-end: ${props => props.$horizontalMargin ? rem(props.$horizontalMargin) : '0'};
  ${props => props.$minHeight ? `min-height: ${rem(props.$minHeight)};` : ''}
`

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
`

const CardTitle = styled(Heading)`
  && {
    font-weight: ${props => props.theme.font.secondary.weight.superBold};
  }
`

const CardLabel = styled(Label)`
  position: absolute;
  top: 0;
  inset-inline-start: 50%;
  transform: translate(var(--lux-plus-promo-card-label-translate-x), -50%);
  z-index: 1;

  --lux-plus-promo-card-label-translate-x: -50%;

  [dir='rtl'] & {
    --lux-plus-promo-card-label-translate-x: 50%;
  }
`

const CardPaginationDots = styled(PaginationDots)`
  && {
    margin-top: ${rem(16)};
    --pagination-dot-bg: transparent;
    --pagination-dot-fill: ${props => props.theme.palette.neutral.surface.darkForeground};
    --pagination-dot-border-thickness: 1px;
  }
`

// ===== Types
interface CarouselProps {
  horizontalMargin: number
  activeIndex: number
  count: number
  onPipClick: (index: number) => void
  onHoverChange: (hovered: boolean) => void
  showPips: boolean
  slideDuration: number
  paused: boolean
}

interface Props {
  promotion: LandingPagePromotion
  /** Minimum height for consistent card sizing in carousel */
  minHeight?: number
  /** Ref callback for card height measurement */
  cardRef?: (el: HTMLDivElement | null) => void
  /** Carousel-related props (optional - only needed when used in carousel) */
  carousel?: CarouselProps
}

// ===== Component
function LuxPlusPromoCard({
  promotion,
  minHeight,
  carousel,
  cardRef,
}: Props) {
  const { label, title, subtitle } = promotion
  const promoCode = promotion.type === PROMO_CODE_PROMOTION ? promotion.promoCode : ''
  const { isCopied, handleCopy } = useCopyWithFeedback(promoCode)
  const showModal = useContext(ModalContext)
  const intl = useIntl()

  const handleCtaClick = useCallback(() => {
    if (promotion.type === CTA_PROMOTION) {
      window.open(promotion.ctaLink, '_blank')
    }
  }, [promotion])

  const handleFlightVoucherClick = useCallback(() => {
    showModal(<LuxPlusFlightVoucherModal />)
  }, [showModal])

  const handleMouseEnter = useCallback(() => {
    carousel?.onHoverChange(true)
  }, [carousel])

  const handleMouseLeave = useCallback(() => {
    carousel?.onHoverChange(false)
  }, [carousel])

  const handleConditionsClick = useCallback(() => {
    if (promotion.type === PROMO_CODE_PROMOTION) {
      showModal(
        <LuxPlusPromoCodeModal
          title={title}
          promoCode={promotion.promoCode}
          termsAndConditions={promotion.termsAndConditions}
          primaryActionText={promotion.primaryActionText}
          primaryActionHref={promotion.primaryActionHref}
        />,
      )
    } else if (promotion.type === FLIGHT_VOUCHER_PROMOTION) {
      window.open('/lux-plus-terms-and-conditions', '_blank')
    } else {
      window.open('/terms-and-conditions', '_blank')
    }
  }, [promotion, showModal, title])

  return (
    <CardWrapper onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Card
        ref={cardRef}
        padding="32 28 20"
        background="highlight-primary"
        paletteType="default"
        $minHeight={minHeight}
        $horizontalMargin={carousel?.horizontalMargin}
      >
        <CardLabel kind="primary" variant="highlight">
          {label}
        </CardLabel>

        <CardBody>
          <Group direction="vertical" gap={16}>
            <Group direction="vertical" gap={8}>
              <CardTitle variant="heading4" align="center" colour="highlight-primary-contrast">
                {title}
              </CardTitle>
              <Group direction="vertical">
                <BodyText variant="medium" align="center" colour="highlight-primary-contrast">
                  {subtitle}
                </BodyText>
                <BodyText variant="medium" align="center" colour="highlight-primary-contrast">
                  <TextLink
                    size="medium"
                    weight="regular"
                    onClick={handleConditionsClick}
                  >
                    {intl.formatMessage({
                      id: 'Mfiaq_6IoeSH31TebQVi',
                      defaultMessage: 'T&Cs apply.',
                      description: 'Link to view terms and conditions for the LuxPlus promo card',
                    })}
                  </TextLink>
                </BodyText>
              </Group>
            </Group>

            {promotion.type === PROMO_CODE_PROMOTION && (
              <TextButton
                kind="secondary"
                variant="default"
                size="large"
                shape="square"
                endIcon={isCopied ? <LineCheckIcon /> : <LineCopyIcon />}
                isSelected={isCopied}
                fit="full-width"
                onClick={handleCopy}
              >
                {promotion.promoCode}
              </TextButton>
            )}

            {promotion.type === FLIGHT_VOUCHER_PROMOTION && (
              <TextButton
                kind="secondary"
                variant="default"
                size="large"
                shape="square"
                fit="full-width"
                onClick={handleFlightVoucherClick}
                endIcon={<LineAngleRightIcon />}
              >
                {intl.formatMessage(commonMessages.viewVouchers)}
              </TextButton>
            )}

            {promotion.type === CTA_PROMOTION && (
              <TextButton
                kind="secondary"
                variant="default"
                size="large"
                shape="square"
                fit="full-width"
                onClick={handleCtaClick}
                endIcon={<LineAngleRightIcon />}
              >
                {promotion.ctaText}
              </TextButton>
            )}
          </Group>
        </CardBody>

        {carousel?.showPips && (
          <CardPaginationDots
            activeIndex={carousel.activeIndex}
            count={carousel.count}
            onSelect={carousel.onPipClick}
            slideDuration={carousel.slideDuration}
            paused={carousel.paused}
          />
        )}
      </Card>
    </CardWrapper>
  )
}

export default LuxPlusPromoCard
